import { ref, onMounted, onUnmounted } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import productXapiFetchSmall from '../services/api';

const MAX_PROS_ITEMS = 15;

const updateFocusedElement = () => {
  if (!document?.activeElement || document.activeElement.tabIndex !== -1
    || !document.activeElement.closest('.pros-collection .product-thumbnail-container')) { return; }
  const elementToFocus = document.activeElement.closest('.pros-collection .product-thumbnail-container').querySelector('[tabindex="0"]');
  if (elementToFocus) {
    elementToFocus.focus();
  }
};

const updateLinksTabIndex = (carouselContRef, context) => {
  const prodImages = Array.from(carouselContRef?.value?.querySelectorAll('.v-product-thumbnail-image-container') || []);
  prodImages.forEach((prodImage) => {
    if (prodImage) {
      const prodImageLink = prodImage.closest('a');
      if (prodImageLink) {
        prodImageLink.tabIndex = '-1';
      }
    }
  });
  const prodNameEls = Array.from(carouselContRef?.value?.querySelectorAll('a.brand-and-name') || []);
  prodNameEls.forEach((prodNameEl) => {
    if (prodNameEl) {
      prodNameEl.tabIndex = '-1';
      const thumbnailItemEl = prodNameEl?.closest('.product-thumbnail-container');
      if (thumbnailItemEl) {
        const wishListEl = thumbnailItemEl.querySelector('.wish-list-added') || thumbnailItemEl.querySelector('.wish-list-removed');
        const productDescriptionEl = thumbnailItemEl.querySelector('.product-description');
        if (productDescriptionEl) {
          if (wishListEl) {
            productDescriptionEl.tabIndex = wishListEl.tabIndex;
          } else if (context) {
            const productIdEl = thumbnailItemEl.querySelector('[id*="product-thumbnail-"]');
            if (productIdEl) {
              const id = productIdEl.id.replace('product-thumbnail-', '');
              const currentProductIndex = context.ids.indexOf(Number(id)) > -1
                ? context.ids.indexOf(Number(id))
                : context.ids.indexOf(String(id));

              const tabIndexEnabled = currentProductIndex
                >= Math.min(Math.floor(context.showIndex), Math.round(context.ids?.length - context.itemsPerPage))
                && currentProductIndex <= Math.floor(context.showIndex + context.itemsPerPage);
              productDescriptionEl.tabIndex = tabIndexEnabled ? 0 : -1;
            }
          }
        }
      }
    }
  });
  updateFocusedElement();
};

const fetchProducts = async (context, urlParams, includeMetaData, showBopsOnlyProducts, colorSwatchEnhancement) => {
  const isIdSelectedColorMap = Object.keys(context.idSelectedColorMap)?.length > 0;
  // Recursive call making batch requests until desired toLoad of valid products is retrieved
  async function fetchNewItems(validProducts = []) {
    if (
      validProducts.length >= context.toLoad
      || context.loadIndex >= context.ids.length
    ) {
      return validProducts;
    }

    const numberOfProductsNeeded = context.toLoad - validProducts.length;
    let end = !Number.isNaN(numberOfProductsNeeded)
      ? context.loadIndex + numberOfProductsNeeded
      : context.ids.length;

    end = Math.min(end, context.ids.length);
    const fetchedProducts = await Promise.all(context.ids
      .slice(context.loadIndex, end)
      .map((id) => {
        const extraParams = { ...urlParams, ...isIdSelectedColorMap && { swatchColor: context.idSelectedColorMap[id] } };
        return productXapiFetchSmall(id, extraParams, includeMetaData);
      }));

    context.loadIndex += end - context.loadIndex;

    const modifiedProducts = fetchedProducts.filter((product) => {
      const isAvailable = product?.availability?.available;
      const isBopsAvailable = showBopsOnlyProducts
        ? product?.availability?.bopsAvailability
        : true;
      return isAvailable && isBopsAvailable;
    });
    context.loaded += modifiedProducts.length;
    return fetchNewItems(validProducts.concat(modifiedProducts));
  }

  const fetchedProducts = await fetchNewItems();
  return fetchedProducts.map((product, index) => context.restructureProduct(product, includeMetaData, index, colorSwatchEnhancement));
};

// get productId and respective product positions in carousel
const getProductPositionAndId = (start, carouselProducts, itemsPerPage) => {
  let index = start;
  const productsOnPage = carouselProducts.slice(index, index + itemsPerPage).map((product) => (
    {
      productId: product.id,
      // eslint-disable-next-line
      pos: ++index,
    }));
  return productsOnPage;
};

export default function useCarouselProduct(
  numberOfItems,
  inputProducts,
  inputProductids,
  componentContext,
  restructureProduct,
  urlParams,
  isServerSide,
  ssrSize,
  filterFetchedProducts = (p) => p,
  idSelectedColorMap = {},
  includeMetaData,
  carouselContRef,
  showBopsOnlyProducts,
  isBcom,
  colorSwatchEnhancement,
) {
  const carouselProducts = ref([]);
  const loading = ref(true);
  const error = ref('');
  const context = {
    // all products ids to be loaded
    ids: [],
    // start index of ids to load product
    loadIndex: 0,
    // number of product to fetch per batch
    toLoad: 0,
    // start index to show product in carousel
    showIndex: 0,
    // number of items per carousel page
    itemsPerPage: 0,
    // count of products already fetched
    loaded: 0,
    // transformer product according to product thumbnail interface
    restructureProduct,
  };
  const setContext = () => {
    let deviceType = 'large';
    if (window.innerWidth < 600) {
      deviceType = 'small';
    } else if (window.innerWidth >= 600 && window.innerWidth < 1024) {
      deviceType = 'medium';
    }
    // need to load one more item to make carousel right clickable
    if (inputProductids?.length > 0) {
      // type === 'pros'  type === 'rvi'
      context.toLoad = isBcom ? 18 : 16;
    } else {
      context.toLoad = Math.ceil(numberOfItems[deviceType]) + 1;
    }
    context.itemsPerPage = numberOfItems[deviceType];
  };

  const initCarouselProducts = () => {
    if (inputProducts?.length > 0) {
      // type === 'monetization
      const products = inputProducts.map((product, index) => restructureProduct(product, includeMetaData, index));
      carouselProducts.value.push(...products);
    }
    if (carouselProducts.value.length > 0) {
      loading.value = false;
      // emit event on product load for analytics
      componentContext.emit('panelViewEvent');
      // emit productIds and positions on carousel load
      componentContext.emit('pixelPresentedEvent', getProductPositionAndId(0, carouselProducts.value, context.itemsPerPage));
      componentContext.emit('dataLoadStatusEvent', true);
    }
    if (!carouselProducts.value.length) {
      error.value = 'Error loading';
      componentContext.emit('dataLoadStatusEvent', false);
      loading.value = false;
    }
  };
  if (isServerSide) {
    context.toLoad = Math.ceil(numberOfItems[ssrSize]) + 1;
    context.itemsPerPage = numberOfItems[ssrSize];
    initCarouselProducts();
  }
  onMounted(async () => {
    setContext();
    window.addEventListener(
      'resize',
      useDebounceFn(() => setContext(), 250),
    );
    if (!isServerSide) {
      if (inputProductids?.length > 0) {
        // type === 'pros'  type === 'rvi'
        context.ids = inputProductids;
        context.idSelectedColorMap = idSelectedColorMap;
        const products = await fetchProducts(context, urlParams, includeMetaData, showBopsOnlyProducts, colorSwatchEnhancement);
        const filteredProducts = filterFetchedProducts(products);
        carouselProducts.value.push(...filteredProducts);
      }
      initCarouselProducts();
    }
    setTimeout(() => updateLinksTabIndex(carouselContRef, context), 1000);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', useDebounceFn);
  });

  const nextClick = async () => {
    if (
      (inputProductids && inputProductids.length !== 0)
      || (inputProducts && inputProducts.length !== 0)
    ) {
      if (inputProductids && inputProductids.length !== 0) {
        // type === 'pros'  type === 'rvi'
        if (context.loaded - context.showIndex <= 2 * context.itemsPerPage) {
          carouselProducts.value.push(...(await fetchProducts(context, urlParams)));
        }
      }
      context.showIndex = Math.min(
        context.showIndex + context.itemsPerPage,
        MAX_PROS_ITEMS,
      );
      // emit productIds and respective position number on next click
      componentContext.emit('scrollEvent', getProductPositionAndId(context.showIndex, carouselProducts.value, context.itemsPerPage));
      componentContext.emit('onRightChevronClick');
    }
    setTimeout(() => updateLinksTabIndex(carouselContRef, context), 1000);
  };

  const prevClick = () => {
    if (
      (inputProductids && inputProductids.length !== 0)
      || (inputProducts && inputProducts.length !== 0)
    ) {
      context.showIndex = Math.max(context.showIndex - context.itemsPerPage, 0);
      componentContext.emit('onLeftChevronClick');
    }
    setTimeout(() => updateLinksTabIndex(carouselContRef, context), 1000);
  };

  return {
    loading,
    error,
    carouselProducts,
    onMounted,
    onUnmounted,
    nextClick,
    prevClick,
    updateLinksTabIndex,
  };
}
